import { useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Loader2,
	StickyNote,
	Mail,
	Phone,
	CheckSquare,
	CalendarDays,
	Briefcase,
	Building2,
	UserCircle,
	Trophy,
	XCircle,
	ExternalLink,
	Pen,
	Plus,
	Trash2,
	Users
} from 'lucide-react';
import {
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	useToast
} from '@hiring-intelligence/ui';
import {
	DEAL_STAGE_OPTIONS,
	DEAL_STATUS_OPTIONS,
	SERVICE_TYPE_OPTIONS,
	PRIORITY_OPTIONS,
	DEAL_WON_STAGE,
	DEAL_LOST_STAGE,
	YEARS_OF_EXPERIENCE_LABELS,
	DIRECT_REPORTS_LABELS,
	formatNumber,
	parseFormattedNumber
} from '@hiring-intelligence/shared';
import { format } from 'date-fns';
import { crmDealsApi, companyApi, crmContactsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { ActivityLog, type ActivityLogHandle } from '@/components/crm/ActivityLog';
import {
	CrmDetailLayout,
	IdentityHeader,
	QuickActionsBar,
	defaultMoreMenu,
	AboutCard,
	EditableTextField,
	EditableTextarea,
	EditableSelectField,
	ReadOnlyField,
	DataHighlights,
	AssociationCard,
	AssociationItem,
	DetailTabs,
	AiSummaryCard,
	ContactPicker,
	type DetailTabDef
} from '@/components/crm/layout';

const NOT_SET = '__none__';
const PRIORITY_DOT: Record<string, string> = { low: 'bg-slate-400', medium: 'bg-amber-500', high: 'bg-red-500' };
const STAGE_VARIANT: Record<string, any> = {
	lead: 'secondary', qualified: 'secondary', proposal: 'default', interviewing: 'default',
	offer: 'default', placed: 'success', lost: 'destructive'
};
const stageLabel = (v?: string | null) => DEAL_STAGE_OPTIONS.find((o) => o.value === v)?.label || v || '';
const dateFmt = (d?: string | Date | null) => { try { return d ? format(new Date(d), 'd MMM yyyy, HH:mm') : null; } catch { return null; } };

function asSelectOptions<T extends { value: string; label: string }>(opts: readonly T[], notSetLabel = 'Not set') {
	return [{ value: NOT_SET, label: notSetLabel }, ...opts];
}

function formatSalary(sr?: { min?: number; max?: number; currency?: string } | null): string | null {
	if (!sr || (!sr.min && !sr.max)) return null;
	const cur = sr.currency || 'IDR';
	const min = sr.min ? formatNumber(sr.min) : '';
	const max = sr.max ? formatNumber(sr.max) : '';
	if (min && max) return `${cur} ${min}–${max}`;
	return `${cur} ${min || max}`;
}

interface StageRow { name: string; interviewer?: string; feedback?: string }

export default function DealDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const currentUser = useAuthStore((s) => s.user);
	const activityRef = useRef<HTMLDivElement>(null);
	const activityLogRef = useRef<ActivityLogHandle>(null);
	const [tab, setTab] = useState('overview');
	const [loseOpen, setLoseOpen] = useState(false);
	const [lostReason, setLostReason] = useState('');
	const [advancedEditOpen, setAdvancedEditOpen] = useState(false);
	const [advancedDraft, setAdvancedDraft] = useState<any>(null);
	const [confirmStage, setConfirmStage] = useState<string | null>(null);
	const [addHrOpen, setAddHrOpen] = useState(false);
	const [hrDraft, setHrDraft] = useState({ roleTitle: '', yearsOfExperience: '2_to_5_years', directReports: 'individual_contributor', meetingNotes: '' });

	const { data, isLoading } = useQuery({
		queryKey: ['crm-deal', id],
		queryFn: () => crmDealsApi.get(id!),
		enabled: !!id
	});
	const deal: any = data?.data;

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ['crm-deal', id] });
		queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
		queryClient.invalidateQueries({ queryKey: ['crm-deals-board'] });
	};

	const updateMutation = useMutation({
		mutationFn: (patch: Record<string, any>) => crmDealsApi.update(id!, patch),
		onSuccess: () => { invalidate(); toast({ title: 'Saved' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const advancedMutation = useMutation({
		mutationFn: (payload: any) => {
			const min = parseFormattedNumber(payload.salaryMin);
			const max = parseFormattedNumber(payload.salaryMax);
			const salaryRange = (min || max) ? { min, max, currency: payload.currency || 'IDR' } : null;
			const interviewStages = (payload.interviewStages as StageRow[])
				.map((s) => ({ name: s.name.trim(), interviewer: s.interviewer?.trim() || undefined, feedback: s.feedback?.trim() || undefined }))
				.filter((s) => s.name);
			return crmDealsApi.update(id!, {
				salaryRange,
				feePercentage: payload.feePercentage ? Number(payload.feePercentage) : null,
				interviewStages
			});
		},
		onSuccess: () => { invalidate(); setAdvancedEditOpen(false); toast({ title: 'Deal updated' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const stageMutation = useMutation({
		mutationFn: (stage: string) => crmDealsApi.updateStage(id!, stage),
		onSuccess: (res: any) => {
			invalidate();
			const hr = res.data?.hiringRequest;
			if (hr) {
				toast({ title: 'Deal placed', description: `Hiring Request ${hr.dealCode ? `(${hr.dealCode}) ` : ''}created.` });
				navigate(`/hiring-requests/${hr.id}`);
			} else {
				toast({ title: 'Stage updated' });
			}
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const winMutation = useMutation({
		mutationFn: () => crmDealsApi.win(id!),
		onSuccess: () => { invalidate(); toast({ title: 'Deal won' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const addHrMutation = useMutation({
		mutationFn: (body: Record<string, any>) => crmDealsApi.addHiringRequest(id!, body),
		onSuccess: (res: any) => {
			invalidate();
			const hr = res.data?.hiringRequest;
			toast({ title: 'Hiring Request created', description: hr?.dealCode ? `(${hr.dealCode})` : undefined });
			setAddHrOpen(false);
			if (hr?.id) navigate(`/hiring-requests/${hr.id}`);
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	// "Create new HR" — open the full New Hiring Request form (Hiring Requests page) PRE-FILLED with the
	// deal's company, role title, meeting notes and contacts (as PICs). The user reviews & submits there;
	// on submit the new HR is linked back to this deal. (Does NOT auto-create.)
	const createHrDirect = () => {
		if (deal.company?.research?.status !== 'completed') {
			toast({
				title: 'Company research not ready',
				description: `Company research for ${deal.company?.name || 'this company'} must be completed before you can create a hiring request.`,
				variant: 'destructive'
			});
			return;
		}
		navigate('/hiring-requests', {
			state: {
				dealPrefill: {
					dealId: deal.id,
					companyId: deal.companyId || undefined,
					roleTitle: deal.roleTitle || deal.title || '',
					meetingNotes: deal.notes || `Hiring Request for deal: ${deal.title}`,
					serviceType: deal.serviceType || '',
					priority: deal.priority || '',
					feePercentage: deal.feePercentage != null ? String(deal.feePercentage) : '',
					dealNotes: deal.notes || '',
					pic: (deal.contacts || [])
						.filter((c: any) => c.email)
						.map((c: any) => ({ contactId: c.id, name: c.name, email: c.email }))
				}
			}
		});
	};

	const addDealContactMutation = useMutation({
		mutationFn: (contactId: string) => crmDealsApi.addDealContact(id!, contactId),
		onSuccess: () => { invalidate(); toast({ title: 'Contact added' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});
	const removeDealContactMutation = useMutation({
		mutationFn: (contactId: string) => crmDealsApi.removeDealContact(id!, contactId),
		onSuccess: () => { invalidate(); toast({ title: 'Contact removed' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});
	const setPrimaryContactMutation = useMutation({
		mutationFn: (contactId: string) => crmDealsApi.setPrimaryDealContact(id!, contactId),
		onSuccess: () => { invalidate(); toast({ title: 'Primary contact updated' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const loseMutation = useMutation({
		mutationFn: (reason?: string) => crmDealsApi.lose(id!, reason),
		onSuccess: () => { invalidate(); setLoseOpen(false); setLostReason(''); toast({ title: 'Marked as lost' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const deleteMutation = useMutation({
		mutationFn: () => crmDealsApi.remove(id!),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
			queryClient.invalidateQueries({ queryKey: ['crm-deals-board'] });
			toast({ title: 'Deal deleted' });
			navigate('/deals');
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	// For the company select on edit drawer (used when advanced editing or changing associations)
	const { data: companiesData } = useQuery({
		queryKey: ['companies'],
		queryFn: () => companyApi.list(),
		enabled: advancedEditOpen,
		refetchOnWindowFocus: false
	});

	const { data: contactsData } = useQuery({
		queryKey: ['crm-contacts', 'by-company', deal?.companyId],
		queryFn: () => crmContactsApi.list({ companyId: deal!.companyId, limit: 100 }),
		enabled: advancedEditOpen && !!deal?.companyId,
		refetchOnWindowFocus: false
	});

	const save = (key: string) => (raw: string) => {
		const value = raw === NOT_SET ? null : raw.trim() === '' ? null : raw;
		updateMutation.mutate({ [key]: value });
	};

	const handleStageChange = (newStage: string) => {
		if (newStage === NOT_SET || !deal) return;
		if (newStage === deal.stage) return;
		if (newStage === DEAL_WON_STAGE || newStage === DEAL_LOST_STAGE) {
			setConfirmStage(newStage);
			return;
		}
		stageMutation.mutate(newStage);
	};

	const openAdvanced = () => {
		if (!deal) return;
		setAdvancedDraft({
			salaryMin: deal.salaryRange?.min ? formatNumber(deal.salaryRange.min) : '',
			salaryMax: deal.salaryRange?.max ? formatNumber(deal.salaryRange.max) : '',
			currency: deal.salaryRange?.currency || 'IDR',
			feePercentage: deal.feePercentage != null ? String(deal.feePercentage) : '',
			interviewStages: (deal.interviewStages || []).map((s: StageRow) => ({ name: s.name || '', interviewer: s.interviewer || '', feedback: s.feedback || '' }))
		});
		setAdvancedEditOpen(true);
	};

	const scrollToActivity = () => activityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	const startActivity = (type: string) => { activityLogRef.current?.startActivity(type); scrollToActivity(); };

	if (isLoading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
	if (!deal) return (
		<div className="p-6">
			<Link to="/deals" className="text-sm text-primary hover:underline">← Deals</Link>
			<p className="mt-6 text-center text-muted-foreground">Deal not found.</p>
		</div>
	);

	const salary = formatSalary(deal.salaryRange);
	const interviewStages: StageRow[] = deal.interviewStages || [];
	const isClosed = deal.stage === DEAL_WON_STAGE || deal.stage === DEAL_LOST_STAGE;
	const numAssociatedContacts = deal.contacts?.length || 0;

	const left = (
		<>
			<IdentityHeader
				backTo="/deals"
				backLabel="Deals"
				avatar={
					<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
						<Briefcase className="h-6 w-6" />
					</div>
				}
				title={deal.title}
				titleBadges={
					<>
						<Badge variant={STAGE_VARIANT[deal.stage] || 'secondary'}>{stageLabel(deal.stage)}</Badge>
						{deal.priority && (
							<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
								<span className={`h-2 w-2 rounded-full ${PRIORITY_DOT[deal.priority] || 'bg-muted'}`} />
								{PRIORITY_OPTIONS.find((o) => o.value === deal.priority)?.label}
							</span>
						)}
					</>
				}
				subtitle={
					<>
						{salary && <span>{salary}</span>}
						{deal.targetStartDate && (
							<span>{salary ? ' · ' : ''}Close {dateFmt(deal.targetStartDate)}</span>
						)}
					</>
				}
				metaRow={
					<>
						{deal.roleTitle && <span>{deal.roleTitle}</span>}
						{deal.company?.name && (
							<>
								{deal.roleTitle && ' · '}
								<Link to={`/companies/${deal.companyId}`} className="hover:underline">{deal.company.name}</Link>
							</>
						)}
					</>
				}
				actions={[
					...(isClosed ? [] : [
						{ label: 'Mark as Won', icon: <Trophy className="h-4 w-4" />, onClick: () => winMutation.mutate() },
						{ label: 'Mark as Lost', icon: <XCircle className="h-4 w-4" />, onClick: () => setLoseOpen(true) }
					]),
					{ label: 'Edit salary + stages', icon: <Pen className="h-4 w-4" />, onClick: openAdvanced },
					...(currentUser?.role === 'super_admin' ? [{
						label: 'Delete deal',
						icon: <Trash2 className="h-4 w-4" />,
						onClick: () => { if (window.confirm('Delete this deal? This cannot be undone.')) deleteMutation.mutate(); },
						destructive: true
					}] : [])
				]}
			/>

			<QuickActionsBar
				actions={[
					{ label: 'Note', icon: StickyNote, onClick: () => startActivity('note') },
					{ label: 'Email', icon: Mail, onClick: () => startActivity('email') },
					{ label: 'Call', icon: Phone, onClick: () => startActivity('call') },
					{ label: 'Task', icon: CheckSquare, onClick: () => startActivity('task') },
					{ label: 'Meeting', icon: CalendarDays, onClick: () => startActivity('meeting') }
				]}
				moreMenu={defaultMoreMenu({ onLogSMS: () => startActivity('sms'), onLogLinkedIn: () => startActivity('linkedin'), onLogWhatsApp: () => startActivity('whatsapp') })}
			/>

			<AboutCard title="About this deal">
				<EditableTextField label="Deal Name" value={deal.title} onSave={save('title')} placeholder="Deal name" />
				<EditableTextField label="Role Title" value={deal.roleTitle} onSave={save('roleTitle')} placeholder="e.g. Chief Financial Officer" />
				<EditableSelectField
					label="Deal Stage"
					value={deal.stage}
					options={DEAL_STAGE_OPTIONS as any}
					onSave={handleStageChange}
					renderDisplay={(v) => <Badge variant={STAGE_VARIANT[v as string] || 'secondary'}>{stageLabel(v as string)}</Badge>}
				/>
				<EditableSelectField
					label="Status"
					value={deal.status}
					options={DEAL_STATUS_OPTIONS as any}
					onSave={save('status')}
				/>
				<EditableSelectField
					label="Priority"
					value={deal.priority}
					options={PRIORITY_OPTIONS as any}
					onSave={save('priority')}
					renderDisplay={(v) => v ? (
						<span className="inline-flex items-center gap-1 text-sm">
							<span className={`h-2 w-2 rounded-full ${PRIORITY_DOT[v as string] || 'bg-muted'}`} />
							{PRIORITY_OPTIONS.find((o) => o.value === v)?.label}
						</span>
					) : <span className="text-sm text-muted-foreground italic">—</span>}
				/>
				<EditableSelectField
					label="Service Type"
					value={deal.serviceType}
					options={asSelectOptions(SERVICE_TYPE_OPTIONS)}
					onSave={save('serviceType')}
				/>
				<ReadOnlyField label="Salary Range">
					{salary || <span className="text-muted-foreground italic">Not set</span>}
					<button onClick={openAdvanced} className="ml-2 text-xs text-primary hover:underline">edit</button>
				</ReadOnlyField>
				<EditableTextField label="Fee %" value={deal.feePercentage != null ? String(deal.feePercentage) : ''} onSave={(v) => updateMutation.mutate({ feePercentage: v ? Number(v) : null })} placeholder="e.g. 20" type="text" />
				<ReadOnlyField label="Number of Associated Contacts">
					{numAssociatedContacts}
				</ReadOnlyField>
				<ReadOnlyField label="Deal Owner">
					{deal.owner?.name || <span className="text-muted-foreground italic">No owner</span>}
				</ReadOnlyField>
				<EditableTextarea label="Deal Description" value={deal.notes} onSave={save('notes')} placeholder="Anything noteworthy about this deal…" />
			</AboutCard>
		</>
	);

	const hiringRequests: any[] = deal.hiringRequests || [];
	const wonBanner = deal.stage === DEAL_WON_STAGE && (
		<div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
			<div className="flex items-center gap-3 min-w-0">
				<Trophy className="h-5 w-5 text-emerald-600 shrink-0" />
				<div className="min-w-0">
					<p className="font-medium text-emerald-900">Won</p>
					<p className="text-sm text-emerald-700 truncate">
						{hiringRequests.length === 0
							? 'Create the Hiring Request to start recruitment.'
							: 'Hiring Request linked.'}
					</p>
				</div>
			</div>
			{hiringRequests.length === 0 && <Button size="sm" onClick={createHrDirect}>
				<Plus className="mr-1 h-3.5 w-3.5" /> Add HR
			</Button>}
		</div>
	);

	const lostBanner = deal.stage === DEAL_LOST_STAGE && (
		<div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-center gap-2 text-sm text-red-800">
			<XCircle className="h-4 w-4 shrink-0" />
			<span>Lost{deal.lostReason ? `: ${deal.lostReason}` : ''}.</span>
			<Button variant="ghost" size="sm" className="ml-auto h-7" onClick={() => stageMutation.mutate('lead')} disabled={stageMutation.isPending}>Reopen</Button>
		</div>
	);

	const interviewStagesCard = (
		<div className="rounded-lg border bg-card">
			<div className="flex items-center justify-between border-b px-4 py-2.5">
				<div className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4" /> Interview Stages</div>
				<Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={openAdvanced}>
					<Pen className="mr-1 h-3 w-3" /> Edit
				</Button>
			</div>
			<div className="px-4 py-3">
				{interviewStages.length === 0 ? (
					<p className="text-sm text-muted-foreground">No interview stages defined.</p>
				) : (
					<div className="space-y-2">
						{interviewStages.map((s, i) => (
							<div key={i} className="rounded-md border bg-background p-2.5">
								<div className="flex items-center justify-between gap-2">
									<p className="text-sm font-medium">{s.name}</p>
									{s.interviewer && <span className="text-xs text-muted-foreground">{s.interviewer}</span>}
								</div>
								{s.feedback && <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{s.feedback}</p>}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);

	const overviewBody = (
		<>
			{wonBanner}
			{lostBanner}
			<AiSummaryCard timestamp={dateFmt(deal.updatedAt) || undefined}>
				<p className="text-muted-foreground italic">AI deal summary will appear here once activity has been logged.</p>
			</AiSummaryCard>
			<DataHighlights items={[
				{ label: 'Creation Date', value: dateFmt(deal.createdAt) },
				{ label: 'Deal Stage', value: stageLabel(deal.stage) },
				{ label: 'Last Activity Date', value: dateFmt(deal.updatedAt) }
			]} />
			{interviewStagesCard}
			<div ref={activityRef} className="rounded-lg border bg-card">
				<div className="border-b px-4 py-2.5 text-sm font-semibold">Recent activities</div>
				<div className="px-4 py-3"><ActivityLog ref={activityLogRef} dealId={deal.id} /></div>
			</div>
		</>
	);

	const tabs: DetailTabDef[] = [
		{ value: 'overview', label: 'Overview', content: overviewBody },
		{ value: 'activities', label: 'Activities', content: <div className="rounded-lg border bg-card px-4 py-3"><ActivityLog ref={activityLogRef} dealId={deal.id} /></div> }
	];

	const right = (
		<>
			<AssociationCard
				title="Hiring Request"
				count={hiringRequests.length}
				action={
						// Strict 1:1 — only legacy deals without an HR can still create one.
						hiringRequests.length === 0 ? (
							<Button
								variant="ghost"
								size="sm"
								className="h-7 px-2 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary"
								onClick={createHrDirect}
							>
								<Plus className="mr-1 h-3.5 w-3.5" /> Create
							</Button>
						) : undefined
					}
				icon={<Trophy className="h-3.5 w-3.5" />}
				emptyMessage="No Hiring Request yet. Click Create to start recruitment work for this deal."
			>
				{hiringRequests.length > 0 && hiringRequests.map((hr: any) => (
					<AssociationItem
						key={hr.id}
						href={`/hiring-requests/${hr.id}`}
						title={hr.roleTitle}
						meta={hr.dealCode && <div className="truncate">{hr.dealCode}</div>}
						badge={hr.status ? <Badge variant="secondary" className="text-[10px]">{hr.status.replace(/_/g, ' ')}</Badge> : undefined}
					/>
				))}
			</AssociationCard>

			<AssociationCard
				title="Contacts"
				count={deal.contacts?.length || 0}
				icon={<UserCircle className="h-3.5 w-3.5" />}
				emptyMessage="No contacts yet. Add one — deal contacts become the Hiring Request PICs."
			>
				<div className="pb-1">
					<ContactPicker
						companyId={deal.companyId || undefined}
						label="Add contact"
						excludeIds={(deal.contacts || []).map((c: any) => c.id)}
						onSelect={(c) => addDealContactMutation.mutate(c.id)}
					/>
				</div>
				{(deal.contacts || []).map((c: any) => {
					const isPrimary = c.DealContact?.isPrimary || c.id === deal.primaryContactId;
					return (
						<div key={c.id} className="group flex items-start justify-between gap-2 rounded-md border bg-background p-2.5">
							<a href={`/contacts/${c.id}`} className="min-w-0 flex-1">
								<div className="flex items-center gap-1.5">
									<span className="truncate text-sm font-medium text-primary">{c.name}</span>
									{isPrimary && <Badge variant="secondary" className="text-[10px]">Primary</Badge>}
								</div>
								{(c.title || c.email) && (
									<div className="mt-0.5 text-xs text-muted-foreground space-y-0.5">
										{c.title && <div className="truncate">{c.title}</div>}
										{c.email && <div className="truncate">{c.email}</div>}
									</div>
								)}
							</a>
							<div className="flex items-center gap-2 shrink-0">
								{!isPrimary && (
									<button type="button" onClick={() => setPrimaryContactMutation.mutate(c.id)} className="text-[10px] text-primary hover:underline opacity-0 group-hover:opacity-100" title="Set as primary">Set primary</button>
								)}
								<button type="button" onClick={() => removeDealContactMutation.mutate(c.id)} className="opacity-0 group-hover:opacity-100" title="Remove from deal">
									<Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
								</button>
							</div>
						</div>
					);
				})}
			</AssociationCard>

			<AssociationCard
				title="Companies"
				count={deal.company ? 1 : 0}
				icon={<Building2 className="h-3.5 w-3.5" />}
				emptyMessage="No company linked."
			>
				{deal.company && (
					<AssociationItem
						href={`/companies/${deal.companyId}`}
						title={
							<span className="inline-flex items-center gap-1.5">
								{deal.company.name}
								<Badge variant="secondary" className="text-[10px]">Primary</Badge>
							</span>
						}
						meta={deal.company.industry && <div className="truncate">{deal.company.industry}</div>}
					/>
				)}
			</AssociationCard>

			<AssociationCard title="Attachments" emptyMessage="Files attached to deal activities will appear here." />
			<AssociationCard title="Invoices" emptyMessage="Create an invoice to bill your customer and track payment." />
		</>
	);

	return (
		<>
			<CrmDetailLayout
				left={left}
				center={<DetailTabs value={tab} onValueChange={setTab} tabs={tabs} />}
				right={right}
			/>

			{/* Stage transition confirm */}
			<Dialog open={!!confirmStage} onOpenChange={(o) => { if (!o) setConfirmStage(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{confirmStage === DEAL_WON_STAGE ? 'Mark deal as Placed?' : 'Mark deal as Lost?'}
						</DialogTitle>
						<DialogDescription>
							{confirmStage === DEAL_WON_STAGE
								? `Moving "${deal.title}" to Placed will mark it as won and automatically create a Hiring Request.`
								: 'You can add an optional reason after confirming.'}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setConfirmStage(null)}>Cancel</Button>
						<Button onClick={() => {
							if (confirmStage === DEAL_WON_STAGE) winMutation.mutate();
							else if (confirmStage === DEAL_LOST_STAGE) setLoseOpen(true);
							setConfirmStage(null);
						}}>Confirm</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Lose dialog */}
			<Dialog open={loseOpen} onOpenChange={(o) => { setLoseOpen(o); if (!o) setLostReason(''); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Mark deal as Lost?</DialogTitle>
						<DialogDescription>This will close the opportunity. Add an optional reason.</DialogDescription>
					</DialogHeader>
					<div className="grid gap-2 py-2">
						<Label htmlFor="lost-reason">Lost reason (optional)</Label>
						<Textarea id="lost-reason" value={lostReason} onChange={(e) => setLostReason(e.target.value)} rows={3} />
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setLoseOpen(false)}>Cancel</Button>
						<Button variant="destructive" onClick={() => loseMutation.mutate(lostReason.trim() || undefined)} disabled={loseMutation.isPending}>
							{loseMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Mark as Lost
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Advanced edit dialog — salary + interview stages */}
			<Dialog open={advancedEditOpen} onOpenChange={setAdvancedEditOpen}>
				<DialogContent className="max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Edit deal financials & interview stages</DialogTitle>
						<DialogDescription>Update salary range, fee, and interview stage records.</DialogDescription>
					</DialogHeader>
					{advancedDraft && (
						<div className="grid gap-4 py-3">
							<div className="grid gap-2">
								<Label>Salary Range</Label>
								<div className="grid gap-2 sm:grid-cols-3">
									<Input placeholder="Min" value={advancedDraft.salaryMin} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, salaryMin: formatNumber(parseFormattedNumber(e.target.value)) }))} />
									<Input placeholder="Max" value={advancedDraft.salaryMax} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, salaryMax: formatNumber(parseFormattedNumber(e.target.value)) }))} />
									<Input placeholder="Currency" value={advancedDraft.currency} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, currency: e.target.value }))} />
								</div>
							</div>
							<div className="grid gap-2">
								<Label>Fee Percentage (%)</Label>
								<Input type="number" value={advancedDraft.feePercentage} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, feePercentage: e.target.value }))} />
							</div>
							<div className="grid gap-2">
								<div className="flex items-center justify-between">
									<Label>Interview Stages</Label>
									<Button type="button" variant="outline" size="sm" onClick={() => setAdvancedDraft((p: any) => ({ ...p, interviewStages: [...p.interviewStages, { name: '', interviewer: '', feedback: '' }] }))}>
										<Plus className="mr-1 h-3.5 w-3.5" /> Add stage
									</Button>
								</div>
								{advancedDraft.interviewStages.length === 0 && <p className="text-xs text-muted-foreground">No stages yet.</p>}
								{advancedDraft.interviewStages.map((s: StageRow, idx: number) => (
									<div key={idx} className="space-y-2 rounded-lg border p-3">
										<div className="flex items-start gap-2">
											<Input placeholder="Stage name" value={s.name} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, interviewStages: p.interviewStages.map((x: StageRow, i: number) => i === idx ? { ...x, name: e.target.value } : x) }))} />
											<Button type="button" variant="ghost" size="icon" onClick={() => setAdvancedDraft((p: any) => ({ ...p, interviewStages: p.interviewStages.filter((_: StageRow, i: number) => i !== idx) }))}>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
										<Input placeholder="Interviewer (optional)" value={s.interviewer || ''} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, interviewStages: p.interviewStages.map((x: StageRow, i: number) => i === idx ? { ...x, interviewer: e.target.value } : x) }))} />
										<Textarea placeholder="Feedback (optional)" rows={2} value={s.feedback || ''} onChange={(e) => setAdvancedDraft((p: any) => ({ ...p, interviewStages: p.interviewStages.map((x: StageRow, i: number) => i === idx ? { ...x, feedback: e.target.value } : x) }))} />
									</div>
								))}
							</div>
						</div>
					)}
					<DialogFooter>
						<Button variant="outline" onClick={() => setAdvancedEditOpen(false)}>Cancel</Button>
						<Button onClick={() => advancedMutation.mutate(advancedDraft)} disabled={advancedMutation.isPending}>
							{advancedMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Add Hiring Request — creates a new HR linked to this deal */}
			<Dialog open={addHrOpen} onOpenChange={(o) => {
				setAddHrOpen(o);
				if (o) setHrDraft({ roleTitle: deal.roleTitle || deal.title || '', yearsOfExperience: '2_to_5_years', directReports: 'individual_contributor', meetingNotes: deal.notes || `Hiring Request for deal: ${deal.title}` });
			}}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add Hiring Request</DialogTitle>
						<DialogDescription>
							Create a new Hiring Request linked to this deal. Company and primary contact are inherited from the deal — you can edit the rest on the HR page.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-3 py-2">
						<div className="grid gap-1.5">
							<Label htmlFor="hr-role">Role Title</Label>
							<Input id="hr-role" value={hrDraft.roleTitle} onChange={(e) => setHrDraft((d) => ({ ...d, roleTitle: e.target.value }))} placeholder="e.g. Chief Financial Officer" />
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="grid gap-1.5">
								<Label>Years of Experience</Label>
								<Select value={hrDraft.yearsOfExperience} onValueChange={(v) => setHrDraft((d) => ({ ...d, yearsOfExperience: v }))}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										{Object.entries(YEARS_OF_EXPERIENCE_LABELS).map(([v, label]) => (
											<SelectItem key={v} value={v}>{label}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="grid gap-1.5">
								<Label>Direct Reports</Label>
								<Select value={hrDraft.directReports} onValueChange={(v) => setHrDraft((d) => ({ ...d, directReports: v }))}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										{Object.entries(DIRECT_REPORTS_LABELS).map(([v, label]) => (
											<SelectItem key={v} value={v}>{label}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="hr-notes">Meeting notes</Label>
							<Textarea id="hr-notes" rows={3} value={hrDraft.meetingNotes} onChange={(e) => setHrDraft((d) => ({ ...d, meetingNotes: e.target.value }))} placeholder="Context from the initial conversation…" />
						</div>
						{!deal.primaryContact && (
							<p className="text-xs text-amber-700">
								⚠ This deal has no primary contact. Set one in the About card first, or the HR creation will fail (a PIC is required).
							</p>
						)}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setAddHrOpen(false)}>Cancel</Button>
						<Button
							onClick={() => addHrMutation.mutate({
								roleTitle: hrDraft.roleTitle,
								yearsOfExperience: hrDraft.yearsOfExperience,
								directReports: hrDraft.directReports,
								meetingNotes: hrDraft.meetingNotes
							})}
							disabled={addHrMutation.isPending || !hrDraft.roleTitle.trim() || !hrDraft.meetingNotes.trim()}
						>
							{addHrMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
